import React, { useMemo, useState } from 'react';

// Services
import { getActiveGsts, getDocDetails, getRegDetails,updateAddress,upsertDocDetails,upsertRegDetails, verifyGstin } from 'services/onboardingDetails';

// Types
import {  MerchantDoc, MerchantDocsResponseData, RegDetail, RegDetailsResponseData } from 'services/types';
import { FetchActiveGstsPayload, FetchMerchantDocsPayload, FetchRegDetailsPayload, UpdateAddressPayload, UpdateDocDetailsPayload, UpdateRegDetailsPayload, VerifyGstinPayload } from 'contexts/types';

//Context
import { OnboardingContext } from '../contexts/OnboardingContext';

const OnboardingProvider = ({ children }: { children: React.ReactNode }) => {
  const [data, setData] = useState<{
    regDetails: {
      [key: string]: RegDetail;
    };
    merchantDocs: {
      [key: string]: MerchantDoc;
    };
    activeGsts: string[]
  }>({
    regDetails: {},
    merchantDocs: {},
    activeGsts: []
  });
  const formatRegDetails = (data: RegDetail[]) => {
    const regDetails: { [key: string]: RegDetail } = {};
   data.forEach((regDetail: RegDetail) => {
     regDetails[regDetail.regKey] = regDetail;
   })
    return regDetails;
  }
  const formatDocs = (data:MerchantDocsResponseData ) => {
    const merchantDocs: { [key: string]: MerchantDoc } = {};
    data.merchantDocs.forEach((merchantDoc: MerchantDoc) => {
      merchantDocs[merchantDoc.docType] = merchantDoc;
      merchantDocs[merchantDoc.docType].name = merchantDoc.docName;
    })
    return merchantDocs;
  }
  const fetchRegDetails = async (
    {
      regKeys,
      finalCb,
      cb,
      errorCb
    }: FetchRegDetailsPayload,
  ) => {
    try {
      let regDetails: ReturnType<typeof formatRegDetails>
      if (regKeys) {
        const data = await getRegDetails<RegDetail[]>({ regKeys });
        regDetails = formatRegDetails(data);
      } else {
        const data = await getRegDetails<RegDetailsResponseData>({});
        regDetails = formatRegDetails(data.regDetails);
      }
      setData((prev) => ({ ...prev, regDetails:{...prev.regDetails, ...regDetails} }));
      cb?.();
    } catch (error) {
      errorCb?.(error);
    } finally {
      finalCb?.();
    }
  };
  
  const fetchActiveGsts = async (
    { finalCb, cb, errorCb} : FetchActiveGstsPayload
  )=>{
    try {
      const data = await getActiveGsts();
      if(data?.gsts)
        setData((prev) => ({ ...prev, activeGsts: data.gsts }));
      cb?.(data);
    } catch (error) {
      errorCb?.(error);
    }
    finally {
      finalCb?.();
    }
  }

  const fetchMerchantDocs = async (
    {docKey, finalCb, cb, errorCb}:FetchMerchantDocsPayload
  )=>{
    try {
      const data = await getDocDetails({ docKey });
      const merchantDocs = formatDocs(data);
      setData((prev) => ({ ...prev, merchantDocs:{...prev.merchantDocs, ...merchantDocs} }));
      cb?.();
    } catch (error) {
      errorCb?.(error);
    }
    finally {
      finalCb?.();
    }
  }

  const updateRegDetails=async({ payload,finalCb,cb,errorCb}:UpdateRegDetailsPayload)=>{
    try {
      await upsertRegDetails(payload);
      cb?.();
    } catch (error) {
      errorCb?.(error);
    } finally {
      finalCb?.();
    }
  }

  const verifyGstinDetails= async (
    { payload, finalCb ,cb ,errorCb }:
    VerifyGstinPayload) => {
    try {
      await verifyGstin(payload);
      cb?.();
    } catch (error) {
      errorCb?.(error);
    } finally {
      finalCb?.();
    }
  }

  const updateAddressField = async(
    { payload, finalCb , cb , errorCb } : UpdateAddressPayload) => {
    try {
      await updateAddress(payload);
      cb?.();
    } catch (error) {
      errorCb?.(error);
    } finally {
      finalCb?.();
    }
  }

  const updateDocDetails=async({ payload,finalCb,cb,errorCb}:UpdateDocDetailsPayload)=>{
    try {
      await upsertDocDetails(payload);
      const { data }=payload
      const newFileData={}
      for (const key in data) {
        const filedata=data[key]
        if (filedata) {
          const newFile={
            file: filedata.file,
            docName: filedata.fileName,
            name: filedata.fileName,
            docType: key
          }
          newFileData[key]=newFile
        }
      }
      setData((prev) => ({ ...prev, merchantDocs:{...prev.merchantDocs, ...newFileData } }));
      cb?.();
    } catch (error) {
      errorCb?.(error);
    } finally {
      finalCb?.();
    }
  }
  const value = useMemo(
    () => ({
      data,
      fetchRegDetails,
      fetchMerchantDocs,
      fetchActiveGsts,
      updateDocDetails,
      updateRegDetails,
      updateAddressField,
      verifyGstinDetails
    }),
    [data],
  );
  return (
    <OnboardingContext.Provider
      value={value}
    >
      {children}
    </OnboardingContext.Provider>
  );
};

export const withOnboardingProvider = <T=any,>(Component: React.ComponentType) => (props: T) => (
  <OnboardingProvider>
    <Component {...props} />
  </OnboardingProvider>
)

export default OnboardingProvider;
